import React from "react";
import { Modal, Dimensions, Platform } from "react-native";
import styled, { useTheme } from "styled-components/native";
import Animated, { FadeIn, SlideInDown } from "react-native-reanimated";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { DefaultTheme } from "styled-components/native";

type MobileKeyboardAwareSlideSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  headerTitle?: string;
  bottomOffset?: number;
  backgroundColor?: string;
  headerBackgroundColor?: string;
  headerTextColor?: string;
  contentPaddingHorizontal?: number;
  contentPaddingVertical?: number;
};

const windowHeight = Dimensions.get("window").height;

const MobileKeyboardAwareSlideSheet: React.FC<
  MobileKeyboardAwareSlideSheetProps
> = ({
  isOpen,
  onClose,
  children,
  headerTitle,
  bottomOffset = 20,
  backgroundColor,
  headerBackgroundColor,
  headerTextColor,
  contentPaddingHorizontal = SpaceType.Double,
  contentPaddingVertical = SpaceType.Double,
}) => {
  const theme = useTheme();
  const typography = useTypographyStore();

  const handleCloseModal = () => {
    onClose?.();
  };

  return (
    <Modal
      animationType="none"
      transparent={true}
      visible={isOpen}
      onRequestClose={handleCloseModal}
      {...(Platform.OS === "ios" &&
        theme.isTablet && { supportedOrientations: ["landscape", "portrait"] })}
    >
      <ModalBackground entering={FadeIn} />
      <OverlayContainer entering={SlideInDown.duration(300)}>
        <SheetContainer backgroundColor={backgroundColor || theme["core-01"]}>
          <HeaderContainer
            headerBackgroundColor={headerBackgroundColor || theme["core-02"]}
          >
            <HeaderTitle
              textAlign="center"
              type={typography["H2-M"]}
              color={headerTextColor || theme["core-05"]}
              numberOfLines={1}
            >
              {headerTitle}
            </HeaderTitle>
            <CloseButtonContainer onPress={handleCloseModal}>
              <VDBIcon
                name={VDBIcons.CLOSE_CIRCLE}
                variant={IconsVariant.VDBCustom}
                size={24}
                color={theme["core-06"]}
              />
            </CloseButtonContainer>
          </HeaderContainer>
          <LineView />
          <ContentArea
            bottomOffset={bottomOffset}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            <ContentPaddingContainer
              paddingHorizontal={contentPaddingHorizontal}
              paddingVertical={contentPaddingVertical}
            >
              {children}
            </ContentPaddingContainer>
          </ContentArea>
        </SheetContainer>
      </OverlayContainer>
    </Modal>
  );
};

const ModalBackground = styled(Animated.View)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
`;

const OverlayContainer = styled(Animated.View)`
  flex: 1;
  justify-content: flex-end;
`;

const SheetContainer = styled.View<{ backgroundColor?: string }>`
  background-color: ${({ backgroundColor }: { backgroundColor: string }) =>
    backgroundColor};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  max-height: ${windowHeight * 0.9}px;
  overflow: hidden;
`;

const HeaderContainer = styled.View<{ headerBackgroundColor?: string }>`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding-horizontal: ${SpaceType.Double}px;
  padding-vertical: ${SpaceType.Big}px;
  background-color: ${({
    headerBackgroundColor,
  }: {
    headerBackgroundColor: string;
  }) => headerBackgroundColor};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  position: relative;
`;

const HeaderTitle = styled(VDBText)`
  flex: 1;
  margin-horizontal: ${SpaceType.ExtraLarge}px;
`;

const CloseButtonContainer = styled.TouchableOpacity`
  position: absolute;
  right: ${SpaceType.Big}px;
  top: 50%;
  transform: translateY(-12px);
  padding: ${SpaceType.Small}px;
`;

const LineView = styled.View`
  height: 1px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
`;

const ContentArea = styled(KeyboardAwareScrollView)``;

const ContentPaddingContainer = styled.View<{
  paddingHorizontal: number;
  paddingVertical: number;
}>`
  padding-horizontal: ${({
    paddingHorizontal,
  }: {
    paddingHorizontal: number;
  }) => paddingHorizontal}px;
  padding-vertical: ${({ paddingVertical }: { paddingVertical: number }) =>
    paddingVertical}px;
`;

export default MobileKeyboardAwareSlideSheet;
